import { useMemo } from "react";
import type { DataCollectionResponse } from "@/api";
import {
  addDataSourceTeamMember,
  addDataSourceUserMember,
  getDataSourceMembersOptions,
  getDataSourceMembersQueryKey,
  removeDataSourceTeamMember,
  removeDataSourceUserMember,
  updateDataSourceTeamMember,
  updateDataSourceUserMember,
} from "@/api";
import { type MembershipApi, MembershipPanel } from "@/components/build/MembershipPanel";
import { useCurrentUser } from "@/features/account/useCurrentUser";

/** DataSources' Access tab: the membership model, via the shared MembershipPanel. */
export function DataSourceAccessTab({ source }: Readonly<{ source: DataCollectionResponse }>) {
  const me = useCurrentUser();

  const api = useMemo<MembershipApi>(
    () => ({
      membersOptions: () => getDataSourceMembersOptions({ path: { collection_uuid: source.uuid } }),
      membersQueryKey: () => getDataSourceMembersQueryKey({ path: { collection_uuid: source.uuid } }),
      addUser: (id, role) =>
        addDataSourceUserMember({
          path: { collection_uuid: source.uuid },
          body: { userUuid: id, role },
          throwOnError: true,
        }),
      updateUserRole: (id, role) =>
        updateDataSourceUserMember({
          path: { collection_uuid: source.uuid, user_uuid: id },
          body: { role },
          throwOnError: true,
        }),
      removeUser: (id) =>
        removeDataSourceUserMember({ path: { collection_uuid: source.uuid, user_uuid: id }, throwOnError: true }),
      updateTeamRole: (id, role) =>
        updateDataSourceTeamMember({
          path: { collection_uuid: source.uuid, team_uuid: id },
          body: { role },
          throwOnError: true,
        }),
      removeTeam: (id) =>
        removeDataSourceTeamMember({ path: { collection_uuid: source.uuid, team_uuid: id }, throwOnError: true }),
      addTeam: (id, role) =>
        addDataSourceTeamMember({
          path: { collection_uuid: source.uuid },
          body: { teamUuid: id, role },
          throwOnError: true,
        }),
    }),
    [source.uuid],
  );

  return <MembershipPanel api={api} currentUserUuid={me.data?.user.uuid} />;
}
